import pMap from 'p-map'
import type {FetchFn} from '@wix/document-manager-extensions'
import {ReportableError} from '@wix/document-manager-utils'
export type PageLoadedCallback = (pageJson: any, pageId: string) => void
import type {FragmentListItem} from '@wix/document-services-types'
interface FragmentResult {
    type: string
    name: string
    content: any
}

export type FragmentFetchFallbackFn = (fragmentType: string, fragmentName: string, jsonFilename: string) => Promise<any>

export interface FetchFragmentsFacade {
    fetchFragments(
        fetchFn: FetchFn,
        fragmentsList: FragmentListItem[],
        urlTemplates: string[],
        concurrency?: number,
        fragmentFetchFallbackFn?: FragmentFetchFallbackFn
    ): Promise<FragmentResult[]>
}

export class DefaultFetchFragmentsFacade implements FetchFragmentsFacade {
    async fetchFragments(
        fetchFn: FetchFn,
        fragmentsList: FragmentListItem[],
        urlTemplates: string[],
        concurrency: number = 100,
        fragmentFetchFallbackFn?: FragmentFetchFallbackFn
    ): Promise<FragmentResult[]> {
        return fetchFragments(fetchFn, fragmentsList, urlTemplates, concurrency, fragmentFetchFallbackFn)
    }
}

async function fetchFragments(
    fetchFn: FetchFn,
    fragmentsList: FragmentListItem[],
    urlTemplates: string[],
    concurrency: number = 100,
    fragmentFetchFallback?: FragmentFetchFallbackFn
): Promise<FragmentResult[]> {
    const entries = await pMap(
        fragmentsList,
        ({jsonFilename, fragmentType, fragmentName}) =>
            fetchFragmentJson(fetchFn, urlTemplates, fragmentType, fragmentName, jsonFilename, fragmentFetchFallback),
        {concurrency}
    )

    return entries
}

async function fetchFragmentJson(
    fetchFn: FetchFn,
    urlTemplates: string[],
    fragmentType: string,
    fragmentName: string,
    jsonFilename: string,
    fragmentFetchFallback?: FragmentFetchFallbackFn
): Promise<FragmentResult> {
    for (const urlTemplate of urlTemplates) {
        const url = urlTemplate.replace('{filename}', jsonFilename)
        try {
            const response = await fetchFn(url)
            if (!response) {
                console.warn(`HTTP: failed to fetch fragment ${url}`)
                continue
            }
            if (!response.ok) {
                console.warn(`HTTP: failed to fetch fragment ${url} - ${response.status}: ${response.statusText}`)
                continue
            }
            const fragmentData = await response.json()
            return {type: fragmentType, name: fragmentName, content: fragmentData}
        } catch (e) {
            console.error(`HTTP: failed to fetch fragment ${url}`, e)
        }
    }

    if (fragmentFetchFallback) {
        try {
            const fragmentData = await fragmentFetchFallback(fragmentType, fragmentName, jsonFilename)
            return {type: fragmentType, name: fragmentName, content: fragmentData}
        } catch (e) {
            console.error(`HTTP: fallback fetch failed for fragment ${jsonFilename}`, e)
        }
    }

    throw new ReportableError({
        message: `Failed to fetch fragment using all fallback URLs`,
        errorType: 'failedToFetchFragments',
        extras: {fragmentType, fragmentName, jsonFilename}
    })
}

export {fetchFragments}
